/**
 * API client - handles fetch with consistent config
 * Single responsibility: HTTP communication
 */

type RequestConfig = RequestInit & {
  params?: Record<string, string | number>;
};

async function request<T>(url: string, config: RequestConfig = {}): Promise<T> {
  const { params, ...init } = config;
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...init.headers,
  };

  const response = await fetch(url, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let errorMessage = `API error: ${response.status} ${response.statusText}`;
    try {
      const errData = await response.json();
      if (errData?.message) {
        errorMessage = errData.message;
      }
    } catch {
      // Ignore JSON parse errors for non-JSON error bodies
    }
    const err = new Error(errorMessage) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return response.json() as Promise<T>;
}

export const apiClient = {
  get: <T>(url: string, config?: Omit<RequestConfig, "method" | "body">) =>
    request<T>(url, { ...config, method: "GET" }),

  post: <T>(url: string, body?: unknown, config?: Omit<RequestConfig, "method" | "body">) =>
    request<T>(url, { ...config, method: "POST", body: body ? JSON.stringify(body) : undefined }),
};
